import { REACT_APP_API_URL } from 'helpers/conf';
import { API_URLS, DOMAINS, TEMPORARY_ENV } from 'helpers/constants';
import moment from 'moment';
import qs from 'qs';
import { equals, findIndex, pipe } from 'ramda';

export function isObject(o: any) {
  return o === Object(o) && !Array.isArray(o) && typeof o !== 'function';
}

export function is(x: any, y: any) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  }
  return x !== x && y !== y; // eslint-disable-line no-self-compare
}

export function shallowEqual(objA: any, objB: any) {
  if (is(objA, objB)) return true;

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  const hasOwn = Object.prototype.hasOwnProperty;
  for (let i = 0; i < keysA.length; i++) {
    if (!(hasOwn.call(objB, keysA[i]) && is(objA[keysA[i]], objB[keysA[i]]))) {
      return false;
    }
  }

  return true;
}

export function enumKeys(E: any): string[] {
  return Object.keys(E).filter((k) => typeof E[k] === 'number');
}

export function enumValues(E: any): number[] {
  return enumKeys(E).map((k) => E[k]);
}

export function hexToRGB(hex: string, alpha = 1) {
  const r = Number.parseInt(hex.slice(1, 3), 16),
    g = Number.parseInt(hex.slice(3, 5), 16),
    b = Number.parseInt(hex.slice(5, 7), 16);

  return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
}

export function toCamel(s: string) {
  if (!s.includes('_')) return s;

  return s.replace(/([_][a-z])/gi, ($1) => $1.toUpperCase().replace('_', ''));
}

export function toSnake(s: string) {
  return s.replace(/[\w]([A-Z])/g, (m) => m[0] + '_' + m[1]).toLowerCase();
}

export function camelToNormalize(s: string) {
  return s.replace(/[\w]([A-Z])/g, (m) => m[0] + ' ' + m[1]).toLowerCase();
}

export function deepKeysTransformFactory(transform: (key: string) => string) {
  function deepKeysTransform<T extends { [key: string]: any } | any[]>(
    o: T,
    blacklists?: string[][]
  ): object {
    if (isObject(o)) {
      return Object.keys(o).reduce(
        (previous, current) => {
          const transformedKey = transform(current);

          const keyValue = (o as { [key: string]: any })[current];

          if (blacklists) {
            const keyBlacklists = blacklists
              .filter((bl) => bl[0] === transformedKey)
              .map((bl) => bl.slice(1));

            if (keyBlacklists.find((bl) => bl.length === 0)) {
              previous[transformedKey] = keyValue;
            } else {
              previous[transformedKey] = deepKeysTransform(
                keyValue,
                keyBlacklists
              );
            }
          } else {
            previous[transformedKey] = deepKeysTransform(keyValue);
          }

          return previous;
        },
        {} as { [key: string]: any }
      );
    }
    if (Array.isArray(o)) {
      return o.map((i) => deepKeysTransform(i, blacklists));
    }

    return o;
  }

  return deepKeysTransform;
}

export const camelize = deepKeysTransformFactory(toCamel);
export const snakify = deepKeysTransformFactory(toSnake);

// date: MM/DD/YYYY
export function dateFormat(date: string) {
  const regEx = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const matchResult = date.match(regEx);

  if (
    matchResult != null &&
    matchResult[1] != null &&
    matchResult[2] != null &&
    matchResult[3] != null
  ) {
    return `${matchResult[3]}-${matchResult[2]}-${matchResult[1]}`;
  }

  return date;
}

export function removeSpaces(str: string) {
  return str.replace(/\s/g, '');
}

export function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const isMobileDevice =
  /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/i.test(
    navigator.userAgent
  );

export const getSrc = (src: string) => {
  const { hostname } = window.location;
  const isMainApiUrl = [TEMPORARY_ENV, DOMAINS.LOCAL].some((domain) =>
    hostname.endsWith(domain)
  );

  return `${isMainApiUrl ? API_URLS.MAIN : REACT_APP_API_URL}${src}`;
};

export function addStylesheet(fileName: string) {
  var head = document.head;
  var link = document.createElement('link');

  link.type = 'text/css';
  link.rel = 'stylesheet';
  link.href = fileName;

  head.appendChild(link);
}

export function delay(ms: number) {
  return new Promise((res) => {
    setTimeout(res, ms);
  });
}

export function entries<T extends { [key: string]: any }>(
  obj: T
): [string, T[keyof T]][] {
  let ownProps = Object.keys(obj),
    i = ownProps.length,
    resArray = new Array(i); // preallocate the Array
  while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]];

  return resArray;
}

export function parseFiltersFromQS<T extends string>(
  querystring: string,
  filters: T[]
): Partial<{ [K in T]: string | undefined }> {
  const searchObj = camelize(
    qs.parse(querystring, { ignoreQueryPrefix: true })
  );

  const parsedFilters: { [key: string]: any } = {};

  for (const [filter, value] of entries(searchObj)) {
    if (filters.includes(filter as any)) {
      parsedFilters[filter] = value;
    }
  }
  // @ts-ignore
  return parsedFilters;
}

export function randomNumber(min: number, max: number) {
  return Math.floor(Math.random() * (max - min) + min);
}

export function sortAscending<T, K extends keyof T>(array: T[], key: K) {
  return array.sort((a, b) => {
    let keyA = a[key] as any,
      keyB = b[key] as any;

    if (typeof keyA === 'string' && typeof keyB === 'string') {
      keyA = keyA.toLowerCase();
      keyB = keyB.toLowerCase();
    }

    if (keyA < keyB) return -1;
    if (keyA > keyB) return 1;
    return 0;
  });
}

export function capitalize(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}

export function removeUrlParameters(keys: string[]) {
  // remove URL parameters without refreshing page

  const querystring = window.location.search;

  const searchObj = camelize(
    qs.parse(querystring, { ignoreQueryPrefix: true })
  );

  const stayParams = Object.keys(searchObj)
    .filter((k) => !keys.includes(k))
    .reduce((a, k) => ({ ...a, [k]: (searchObj as any)[k] }), {} as object);

  const searchStringify = qs.stringify(stayParams);

  const pathname =
    window.location.pathname + searchStringify == null
      ? ''
      : `?${searchStringify}`;

  window.history.pushState({}, document.title, pathname);
}

export function stringToUrl(value: string) {
  return value.toLowerCase().replace(/\s/g, '-');
}

export function sortByDate<T>(a: T, b: T, key: keyof T) {
  const dateA = new Date((a[key] as any).toString()).getTime();
  const dateB = new Date((b[key] as any).toString()).getTime();
  return dateA < dateB ? 1 : -1;
}

export function timestampByUTC(time?: moment.MomentInput) {
  return moment(time).utcOffset('00:00').valueOf();
}

export function timestamp(time?: string) {
  return time ? new Date(time).getTime() : new Date().getTime();
}

export function dateByUTC(time?: string) {
  const myDate = time == null ? new Date() : new Date(time);
  const getUTC = myDate.getTime();
  const offset = myDate.getTimezoneOffset() * 60_000;
  return new Date(getUTC + offset);
}

export const revertBoolean = (value: boolean) => !value;

export const findBoolean = <T>(fn: (a: T) => boolean) =>
  pipe(findIndex(fn), equals(-1), revertBoolean);

export const groupsOf =
  (params: { count: number }) =>
  <T>(list: T[]): T[][] =>
    list.reduce(
      (rows, key, index) =>
        (index % params.count === 0
          ? rows.push([key])
          : rows[rows.length - 1].push(key)) && rows,
      [] as any
    );

export const withKey = <T>(list: T[]): { elements: T }[] =>
  list.map((item) => ({ elements: item }));

export const createItems = <T>(item: T, count: number) =>
  Array.apply(null, Array(count)).map((_) => item);

export const groupItems = <T>(items: T[], rows: number, cols = 1) => {
  const chunkSize = rows * cols;

  return Array.from(
    { length: Math.ceil(items.length / chunkSize) },
    (_, i) => ({
      itemsGroup: items.slice(i * chunkSize, i * chunkSize + chunkSize),
      rows,
      cols,
    })
  );
};

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
